import { type Address, GRAPHQL_API_URL, SITE_URL } from "@opensea/utils"
import { APP_NAME, BUILD_ID, ENVIRONMENT } from "@/constants/env"
import { UNLEASH_FRONTEND_API_URL } from "@/constants/unleash"

const isRumEnabled = process.env.IS_RUM_ENABLED

export async function initDatadogRum() {
  if (!isRumEnabled) {
    return
  }

  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID
  if (
    typeof window === "undefined" ||
    ENVIRONMENT === "development" ||
    !clientToken ||
    !applicationId
  ) {
    return
  }

  const { datadogRum } = await import("@datadog/browser-rum")
  const siteOrigin = SITE_URL.origin

  const allowedTracingUrls = [url => url.startsWith(siteOrigin)] as NonNullable<
    Parameters<typeof datadogRum.init>[0]["allowedTracingUrls"]
  >
  if (GRAPHQL_API_URL.origin !== siteOrigin) {
    allowedTracingUrls.push(GRAPHQL_API_URL.toString())
  }
  if (UNLEASH_FRONTEND_API_URL.origin !== siteOrigin) {
    allowedTracingUrls.push(UNLEASH_FRONTEND_API_URL.toString())
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: APP_NAME,
    sessionSampleRate: 1,
    env: ENVIRONMENT,
    version: BUILD_ID,
    useSecureSessionCookie: false,
    trackResources: true,
    proxy: "https://dd2.openseaprorelayproxy.com",
    allowedTracingUrls,
    // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-loading-time-is-calculated
    // exclude all urls from activity. our page is very interactive and there are a lot of things that happen client side,
    // which would make this metric useless. We just care about general page load, e.g after resources were loaded.
    excludedActivityUrls: [_ => true],
  })
}

export const trackAction = async (name: string, context?: object) => {
  const { datadogRum } = await import("@datadog/browser-rum")
  datadogRum.addAction(name, context)
}

export const trackError = async (error: Error, context?: object) => {
  if (!isRumEnabled) {
    return
  }

  const { datadogRum } = await import("@datadog/browser-rum")
  datadogRum.addError(error, context)
}

export const addDurationVital = async (
  name: string,
  options: {
    startTime: number
    duration: number
    context?: object
    description?: string
  },
) => {
  if (!isRumEnabled) {
    return
  }

  const { datadogRum } = await import("@datadog/browser-rum")
  datadogRum.addDurationVital(name, options)
}

export const addTiming = async (name: string) => {
  if (!isRumEnabled) {
    return
  }
  const { datadogRum } = await import("@datadog/browser-rum")
  datadogRum.addTiming(name)
}

export const setUser = async (wallet: Address | undefined) => {
  if (!isRumEnabled) {
    return
  }

  const { datadogRum } = await import("@datadog/browser-rum")
  if (wallet) {
    datadogRum.setUser({ id: wallet })
  } else {
    datadogRum.clearUser()
  }
}
